import { normalizeLocalizedShopText, resolveLocalizedShopText, type LocalizedShopText } from './shopLocalization';

export interface FabricTypeOption {
  id: string;
  fabricCode: string;
  fabricBlend: string;
  fabricBlendTranslations?: LocalizedShopText;
  materialNameTranslations?: Record<string, LocalizedShopText>;
  material?: Record<string, number>;
  colorName?: string | null;
  colorCode?: string | null;
  swatchS3Key?: string | null;
  price?: number | null;
  originInfo?: string | null;
  yarnCount?: number | null;
  weight?: number | null;
  brand?: string | null;
}

export type FabricCodeSelection = [fabricCode: string, colorCodes: string[]];

export function buildFabricPricesPayload(
  selections: FabricCodeSelection[],
  variants: readonly { colorCode: string; price: number | string }[],
) {
  const pricesByColorCode = new Map(
    variants.flatMap(({ colorCode, price }) => {
      const normalizedColorCode = colorCode.trim();
      const parsedPrice = Number(price);
      return normalizedColorCode && Number.isFinite(parsedPrice) && parsedPrice > 0
        ? [[normalizedColorCode.toLowerCase(), Number(parsedPrice.toFixed(2))] as const]
        : [];
    })
  );
  const availableColorCodes = Array.from(pricesByColorCode.keys());

  return Object.fromEntries(
    normalizeFabricCodeSelections(selections).flatMap(([fabricCode, colorCodes]) => {
      const applicableColorCodes = colorCodes.length > 0 ? colorCodes : availableColorCodes;
      const prices = Object.fromEntries(
        applicableColorCodes.flatMap((colorCode) => {
          const price = pricesByColorCode.get(colorCode.toLowerCase());
          return price === undefined ? [] : [[colorCode, price] as const];
        })
      );
      return Object.keys(prices).length > 0 ? [[fabricCode, prices] as const] : [];
    })
  );
}

export function appendFabricCodeSelectionsForColor(
  selections: FabricCodeSelection[],
  nextSelections: FabricCodeSelection[],
  colorCode: string,
): FabricCodeSelection[] {
  const normalizedColorCode = colorCode.trim();
  const next = selections.map(([fabricCode, colorCodes]) => [fabricCode, [...colorCodes]] as FabricCodeSelection);
  const existingFabricCodes = new Set(next.map(([fabricCode]) => fabricCode.toLowerCase()));

  nextSelections.forEach(([fabricCode]) => {
    const normalizedFabricCode = fabricCode.toLowerCase();
    if (existingFabricCodes.has(normalizedFabricCode)) {
      return;
    }

    existingFabricCodes.add(normalizedFabricCode);
    next.push([fabricCode, normalizedColorCode ? [normalizedColorCode] : []]);
  });

  return next;
}

export function autofillEmptyColorVariantFields<
  TVariant extends {
    colorName: string;
    colorCode: string;
    swatchImage: object | null;
  },
>(
  variant: TVariant,
  fabric: Pick<FabricTypeOption, 'fabricCode' | 'colorName' | 'colorCode' | 'swatchS3Key'> | null | undefined,
  createSwatch: (s3Key: string) => NonNullable<TVariant['swatchImage']>
) {
  if (!fabric) return variant;

  return {
    ...variant,
    colorName: variant.colorName.trim() ? variant.colorName : fabric.colorName ?? '',
    colorCode: variant.colorCode.trim() ? variant.colorCode : fabric.colorCode?.trim() || fabric.fabricCode,
    swatchImage: variant.swatchImage ?? (
      fabric.swatchS3Key ? createSwatch(fabric.swatchS3Key) : null
    ),
  } as TVariant;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseJsonValue(value: string) {
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return null;
  }
}

function unwrapPayload(payload: unknown) {
  let current = payload;

  for (let index = 0; index < 6; index += 1) {
    if (typeof current === 'string') {
      const parsed = parseJsonValue(current);
      if (parsed === null) break;
      current = parsed;
      continue;
    }

    if (!isRecord(current)) break;
    if (isRecord(current.data) || Array.isArray(current.data)) {
      current = current.data;
      continue;
    }
    if (isRecord(current.body) || Array.isArray(current.body)) {
      current = current.body;
      continue;
    }
    if (typeof current.body === 'string') {
      const parsed = parseJsonValue(current.body);
      if (parsed === null) break;
      current = parsed;
      continue;
    }
    break;
  }

  return current;
}

function normalizeString(value: unknown, maxLength = 120) {
  return typeof value === 'string' ? value.trim().slice(0, maxLength) : '';
}

function normalizeOptionalNumber(value: unknown) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeMaterial(value: unknown) {
  if (!isRecord(value)) return {};

  return Object.fromEntries(
    Object.entries(value).flatMap(([key, rawValue]) => {
      const materialName = normalizeString(key, 80);
      const percentage = typeof rawValue === 'number' ? rawValue : Number(rawValue);
      return materialName && Number.isFinite(percentage) && percentage >= 0
        ? [[materialName, percentage]]
        : [];
    })
  );
}

function normalizeMaterialNameTranslations(value: unknown) {
  const candidate = typeof value === 'string' ? parseJsonValue(value) : value;
  if (!isRecord(candidate)) return undefined;

  const translations = Object.fromEntries(
    Object.entries(candidate).flatMap(([name, rawTranslations]) => {
      const normalizedName = normalizeString(name, 80).toLowerCase();
      const localizedName = normalizeLocalizedShopText(rawTranslations);
      return normalizedName && localizedName ? [[normalizedName, localizedName] as const] : [];
    })
  );
  return Object.keys(translations).length > 0 ? translations : undefined;
}

export function resolveFabricBlendLabel(fabric: Pick<FabricTypeOption, 'fabricBlend' | 'fabricBlendTranslations'>, locale: string | undefined | null) {
  return resolveLocalizedShopText(fabric.fabricBlendTranslations, locale) || fabric.fabricBlend;
}

export function resolveFabricMaterialLabel(fabric: Pick<FabricTypeOption, 'materialNameTranslations'>, materialName: string, locale: string | undefined | null) {
  const normalizedName = materialName.trim().toLowerCase();
  return resolveLocalizedShopText(fabric.materialNameTranslations?.[normalizedName], locale) || materialName;
}

export function isNotApplicableFabricCode(value: unknown) {
  return normalizeString(value, 80).toLowerCase() === 'na';
}

export function extractFabricTypesFromManageCategoriesPayload(payload: unknown): FabricTypeOption[] {
  const unwrapped = unwrapPayload(payload);
  if (!isRecord(unwrapped) || !Array.isArray(unwrapped.fabric_types)) {
    return [];
  }

  const fabricTypesByCode = new Map<string, FabricTypeOption>();
  unwrapped.fabric_types.forEach((entry) => {
    if (!isRecord(entry)) return;
    const fabricCode = normalizeString(entry.fabric_code, 80);
    if (!fabricCode) return;
    const fabricKey = fabricCode.toLowerCase();
    const material = normalizeMaterial(entry.material);
    const fabricBlendTranslations = normalizeLocalizedShopText(entry.fabric_blend_translations);
    const materialNameTranslations = normalizeMaterialNameTranslations(entry.material_names);
    const colorName = normalizeString(entry.color_name, 80) || null;
    const colorCode = normalizeString(entry.color_code, 40) || null;
    const swatchS3Key = normalizeString(entry.swatch_s3_key, 512) || null;
    const originInfo = normalizeString(entry.origin_info, 500) || null;
    const yarnCountValue = normalizeOptionalNumber(entry.yarn_count);
    const yarnCount = yarnCountValue !== null && Number.isInteger(yarnCountValue) ? yarnCountValue : null;
    const weight = normalizeOptionalNumber(entry.weight);
    const brand = normalizeString(entry.brand, 120) || null;
    const parsedPriceSource = entry.price ?? entry.surcharge;
    const parsedPrice = parsedPriceSource === null || parsedPriceSource === undefined || parsedPriceSource === ''
      ? null
      : Number(parsedPriceSource);
    const nextFabricType: FabricTypeOption = {
      id: normalizeString(entry.id, 120) || fabricCode,
      fabricCode,
      fabricBlend: normalizeString(entry.fabric_blend, 240),
      ...(fabricBlendTranslations ? { fabricBlendTranslations } : {}),
      ...(materialNameTranslations ? { materialNameTranslations } : {}),
      ...(Object.keys(material).length > 0 ? { material } : {}),
      colorName,
      colorCode,
      swatchS3Key,
      price: parsedPrice !== null && Number.isFinite(parsedPrice) ? parsedPrice : null,
      originInfo,
      yarnCount,
      weight,
      brand,
    };
    const existingFabricType = fabricTypesByCode.get(fabricKey);

    fabricTypesByCode.set(fabricKey, existingFabricType ? {
      ...nextFabricType,
      id: existingFabricType.id || nextFabricType.id,
      fabricCode: existingFabricType.fabricCode,
      fabricBlend: existingFabricType.fabricBlend || nextFabricType.fabricBlend,
      fabricBlendTranslations: existingFabricType.fabricBlendTranslations ?? nextFabricType.fabricBlendTranslations,
      materialNameTranslations: {
        ...nextFabricType.materialNameTranslations,
        ...existingFabricType.materialNameTranslations,
      },
      material: existingFabricType.material ?? nextFabricType.material,
      colorName: existingFabricType.colorName ?? nextFabricType.colorName,
      colorCode: existingFabricType.colorCode ?? nextFabricType.colorCode,
      swatchS3Key: existingFabricType.swatchS3Key ?? nextFabricType.swatchS3Key,
      price: existingFabricType.price ?? nextFabricType.price,
      originInfo: existingFabricType.originInfo ?? nextFabricType.originInfo,
      yarnCount: existingFabricType.yarnCount ?? nextFabricType.yarnCount,
      weight: existingFabricType.weight ?? nextFabricType.weight,
      brand: existingFabricType.brand ?? nextFabricType.brand,
    } : nextFabricType);
  });

  return Array.from(fabricTypesByCode.values()).sort((left, right) => left.fabricCode.localeCompare(right.fabricCode, undefined, {
    sensitivity: 'base',
    numeric: true,
  }));
}

export function normalizeFabricCodeSelections(value: unknown): FabricCodeSelection[] {
  const unwrapped = unwrapPayload(value);
  const entries: unknown[] = Array.isArray(unwrapped)
    ? unwrapped
    : isRecord(unwrapped)
      ? ('fabric_code' in unwrapped || 'fabricCode' in unwrapped || 'code' in unwrapped)
        ? [unwrapped]
        : Object.entries(unwrapped).map(([fabricCode, colorCodes]) => [fabricCode, colorCodes])
      : [];

  const seenFabricCodes = new Set<string>();
  return entries.flatMap((entry) => {
    const tuple = Array.isArray(entry)
      ? entry
      : isRecord(entry)
        ? [entry.fabric_code ?? entry.fabricCode ?? entry.code, entry.color_codes ?? entry.colorCodes]
        : [];
    const fabricCode = normalizeString(tuple[0], 80);
    const fabricKey = fabricCode.toLowerCase();
    if (!fabricCode || isNotApplicableFabricCode(fabricCode) || seenFabricCodes.has(fabricKey)) return [];
    seenFabricCodes.add(fabricKey);

    const seenColorCodes = new Set<string>();
    const colorCodes = Array.isArray(tuple[1])
      ? tuple[1].flatMap((colorCode) => {
        const normalizedColorCode = normalizeString(colorCode, 40);
        const colorKey = normalizedColorCode.toLowerCase();
        if (!normalizedColorCode || seenColorCodes.has(colorKey)) return [];
        seenColorCodes.add(colorKey);
        return [normalizedColorCode];
      })
      : [];

    return [[fabricCode, colorCodes] as FabricCodeSelection];
  });
}

export function getFabricCodesForColor(
  selections: FabricCodeSelection[],
  colorCode?: string | null
) {
  const normalizedColorCode = normalizeString(colorCode, 40).toLowerCase();

  return normalizeFabricCodeSelections(selections)
    .filter(([, colorCodes]) => (
      normalizedColorCode
        ? colorCodes.some((code) => code.toLowerCase() === normalizedColorCode)
        : colorCodes.length === 0
    ))
    .map(([fabricCode]) => fabricCode);
}

export function buildFabricCodesPayload(
  selections: FabricCodeSelection[],
  availableColorCodes?: readonly string[]
): FabricCodeSelection[] {
  const normalizedAvailableColors = availableColorCodes
    ? Array.from(new Map(
      availableColorCodes.flatMap((colorCode) => {
        const normalizedColorCode = normalizeString(colorCode, 40);
        return normalizedColorCode ? [[normalizedColorCode.toLowerCase(), normalizedColorCode] as const] : [];
      })
    ).values())
    : null;
  const allowedColors = normalizedAvailableColors
    ? new Set(normalizedAvailableColors.map((colorCode) => colorCode.toLowerCase()))
    : null;
  const normalizedSelections = normalizeFabricCodeSelections(selections).map(([fabricCode, colorCodes]) => [
    fabricCode,
    allowedColors
      ? colorCodes.filter((colorCode) => allowedColors.has(colorCode.toLowerCase()))
      : colorCodes,
  ] as FabricCodeSelection);

  // The add_items backend treats an empty color list as "all colors", and an
  // entirely empty fabric payload as the default NA fabric for all colors.
  // Explicit, non-empty mappings are different: any omitted color gets no
  // inventory row, leaving the image upload without an inventory ID. Preserve
  // the selected mappings and give only those uncovered colors an NA fallback.
  if (
    !normalizedAvailableColors
    || normalizedAvailableColors.length === 0
    || normalizedSelections.length === 0
    || normalizedSelections.some(([, colorCodes]) => colorCodes.length === 0)
  ) {
    return normalizedSelections;
  }

  const assignedColorCodes = new Set(
    normalizedSelections.flatMap(([, colorCodes]) => (
      colorCodes.map((colorCode) => colorCode.toLowerCase())
    ))
  );
  const unassignedColorCodes = normalizedAvailableColors.filter(
    (colorCode) => !assignedColorCodes.has(colorCode.toLowerCase())
  );

  return unassignedColorCodes.length > 0
    ? [...normalizedSelections, ['NA', unassignedColorCodes]]
    : normalizedSelections;
}
